import { useState } from 'react';
import { Copy } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';

const f = (x) => `${x % 12 || 12}${x < 12 ? 'am' : 'pm'}`;
const rangeLabel = (h) => `${f(h)}–${f((h + 1) % 24)}`;

// Pick target hours for copying a slot's pricing (fees, fee days, day prices, waivers).
export function CopySlotPricing({ hour, slots, onCopy }) {
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);
  const others = slots.filter((s) => s.hour !== hour);
  const toggle = (h) => setPicked((p) => (p.includes(h) ? p.filter((x) => x !== h) : [...p, h]));
  const submit = async () => {
    setBusy(true);
    try { await onCopy(hour, picked); setOpen(false); setPicked([]); } catch { /* toast shown by parent */ } finally { setBusy(false); }
  };
  const chip = (label, onClick, testId) => (
    <button type="button" onClick={onClick} className="px-2 py-1 font-mono text-[11px] border border-[#2A0F3D] text-[#A3C7A8] hover:text-[#00F0FF]" data-testid={testId}>{label}</button>
  );
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="px-3 py-1.5 font-mono text-xs border border-[#00F0FF]/60 text-[#00F0FF] hover:bg-[#00F0FF]/10 inline-flex items-center gap-1.5" data-testid={`slot-${hour}-copy`}>
        <Copy className="w-3.5 h-3.5" /> Copy prices to…
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0C0612] border-[#00F0FF]/40 max-w-md" style={{ background: '#0C0612', opacity: 1 }} data-testid={`slot-${hour}-copy-dialog`}>
          <DialogHeader>
            <DialogTitle className="font-mono text-[#00F0FF]">COPY {rangeLabel(hour)} PRICING</DialogTitle>
            <DialogDescription className="font-mono text-[11px] text-[#6B8E73]">
              Copies base, rush hour and long distance (amounts + days) and tier waivers. Uses the saved slot, not unsaved edits. Label, on/off and who can book stay as they are.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap gap-1.5">
            {chip('All other slots', () => setPicked(others.map((s) => s.hour)), `slot-${hour}-copy-all`)}
            {chip('Active slots', () => setPicked(others.filter((s) => s.active).map((s) => s.hour)), `slot-${hour}-copy-active`)}
            {chip('Clear', () => setPicked([]), `slot-${hour}-copy-clear`)}
          </div>
          <div className="grid grid-cols-3 gap-1.5 max-h-72 overflow-y-auto">
            {others.map((s) => (
              <button key={s.hour} type="button" onClick={() => toggle(s.hour)} aria-pressed={picked.includes(s.hour)}
                className={`px-2 py-1.5 font-mono text-[11px] border text-left ${picked.includes(s.hour) ? 'border-[#00F0FF] text-[#050003] bg-[#00F0FF]' : `border-[#2A0F3D] ${s.active ? 'text-[#39FF14]' : 'text-[#6B8E73]'}`}`}
                data-testid={`slot-${hour}-copy-to-${s.hour}`}>
                {rangeLabel(s.hour)}
              </button>
            ))}
          </div>
          <button type="button" disabled={!picked.length || busy} onClick={submit}
            className="h-10 bg-[#00F0FF] text-[#050003] font-mono font-bold uppercase text-xs disabled:opacity-40" data-testid={`slot-${hour}-copy-confirm`}>
            {busy ? 'Copying…' : `Copy to ${picked.length} slot${picked.length === 1 ? '' : 's'}`}
          </button>
        </DialogContent>
      </Dialog>
    </>
  );
}
